import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const images = [
  {
    src: '/lovable-uploads/02cb4f6f-3731-43db-b340-a7bff23cdc6c.png',
    alt: 'Family connection',
    title: 'Understanding Family Bonds'
  },
  {
    src: '/lovable-uploads/3a1a4e86-df2e-4906-b625-660ea8a06e81.png',
    alt: 'Mother and baby',
    title: 'Maternal Connections'
  },
  {
    src: '/lovable-uploads/d2bd82d5-ee57-4639-b57e-e31b2bdd7818.png',
    alt: 'Sisters together',
    title: 'Sibling Relationships'
  },
  {
    src: '/lovable-uploads/e907c7bb-9210-4bcb-8dab-661fb80c2dcc.png',
    alt: 'Mother and daughter',
    title: 'Generational Bonds'
  },
  {
    src: '/lovable-uploads/1821a596-5108-4b71-891c-5a48cf805d82.png',
    alt: 'Father with baby',
    title: 'Paternal Connections'
  }
];

const FlippingGallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);

  const nextImage = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsFlipping(false);
    }, 300);
  };

  const prevImage = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsFlipping(false);
    }, 300);
  };

  const goToImage = (index: number) => {
    if (isFlipping || index === currentIndex) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsFlipping(false);
    }, 300);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6 mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-nearlab-teal-dark mb-4">Family Stories</h2>
        <p className="text-gray-600">
          Every family has a story. DNA testing helps reveal the connections that bind us together.
        </p>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg border">
        <div className="relative h-96 md:h-[500px]">
          <div 
            className={`absolute inset-0 transition-transform duration-300 ${
              isFlipping ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
            }`}
            style={{
              transform: isFlipping ? 'rotateY(90deg)' : 'rotateY(0deg)',
              transformStyle: 'preserve-3d'
            }}
          >
            <img
              src={images[currentIndex].src}
              alt={images[currentIndex].alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <h3 className="text-2xl font-bold mb-2">{images[currentIndex].title}</h3>
              <p className="text-white/90">{images[currentIndex].alt}</p>
            </div>
          </div>

          {/* Navigation Buttons */}
          <Button
            variant="outline"
            size="icon"
            onClick={prevImage}
            disabled={isFlipping}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 border-white/20 text-white hover:bg-white/20"
            aria-label="View previous image"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={nextImage}
            disabled={isFlipping}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 border-white/20 text-white hover:bg-white/20"
            aria-label="View next image"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Thumbnail Navigation */}
        <div className="flex justify-center gap-2 p-4 bg-gray-50">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => goToImage(index)}
              disabled={isFlipping}
              className={`relative w-16 h-16 rounded-lg overflow-hidden transition-all duration-200 ${
                index === currentIndex 
                  ? 'ring-2 ring-nearlab-teal scale-110' 
                  : 'opacity-60 hover:opacity-80'
              }`}
              aria-label={`View image ${index + 1}: ${image.title}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlippingGallery;